import * as styles from "./AbsenceList.css";

import { clsx } from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo, useRef, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@frontend/components/atoms/AlertDialog";
import { Badge, type BadgeVariant } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  List,
  ListEmpty,
  ListItem,
  ListSection,
} from "@frontend/components/atoms/List";
import { Skeleton } from "@frontend/components/atoms/Skeleton";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@frontend/components/atoms/Tabs";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  absenceManagerAtom,
  absenceTypesAtom,
  absencesAtom,
  duplicateAbsenceAtom,
  loadingAtom,
  removeAbsenceAtom,
  sameCaseIdsAtom,
  selectAbsenceAtom,
  selectedIdAtom,
  setTabAtom,
  tabAtom,
  tabCountsAtom,
} from "../stores";
import type {
  Absence,
  AbsenceDraft,
  AbsenceStatus,
  HourStatusFamily,
  ReplacementSlot,
} from "../types";
import { coveredCount, isNewAbsence } from "../utils/draft";
import { formatRange, yearOf } from "../utils/format";

const TABS: {
  value: AbsenceStatus;
  label: React.ReactNode;
  variant: BadgeVariant;
}[] = [
  { value: "current", label: <Trans>Current</Trans>, variant: "warning" },
  { value: "future", label: <Trans>Upcoming</Trans>, variant: "info" },
  { value: "past", label: <Trans>Past</Trans>, variant: "neutral" },
];

interface Group {
  year: string;
  absences: Absence[];
  days: number;
}

function groupByYear(absences: Absence[]): Group[] {
  const groups = new Map<string, Group>();

  const sorted = [...absences].sort((a, b) =>
    (b.beginDate ?? "").localeCompare(a.beginDate ?? ""),
  );

  for (const absence of sorted) {
    const year = yearOf(absence.beginDate);
    const group = groups.get(year) ?? { year, absences: [], days: 0 };

    group.absences.push(absence);
    group.days += absence.dayCount;

    groups.set(year, group);
  }

  return [...groups.values()];
}

function coverage(
  absence: Absence,
  draft: AbsenceDraft | undefined,
  slots: ReplacementSlot[] | undefined,
): React.ReactNode {
  if (slots) {
    return 0 === slots.length ? null : (
      <>
        {" · "}
        <Trans>
          {coveredCount(slots, draft ?? null)}/{slots.length} covered
        </Trans>
      </>
    );
  }

  return 0 === absence.replacedRows ? null : (
    <>
      {" · "}
      <Trans>{absence.replacedRows} replaced</Trans>
    </>
  );
}

/** Nothing to draw until a reason is chosen: a new absence has no type yet. */
function FamilyDot({
  family,
}: {
  family: HourStatusFamily | undefined;
}): React.ReactNode {
  if (undefined === family) {
    return null;
  }

  return (
    <span
      className={clsx(styles.familyDot, styles.familyColor[family])}
      aria-hidden
    />
  );
}

const SKELETON_ROWS = 6;

function AbsenceRowsSkeleton() {
  return (
    <List className={styles.list} separators aria-busy>
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <ListItem
          key={index}
          startSection={
            <Skeleton radius="sm" className={styles.skeletonDays} />
          }
        >
          <Skeleton className={styles.skeletonRange} />
          <Skeleton className={styles.skeletonMeta} />
        </ListItem>
      ))}
    </List>
  );
}

function AbsenceRows({ status }: { status: AbsenceStatus }) {
  const { t } = useLingui();

  // A draft that never reached the server has nothing to confirm
  const [confirming, setConfirming] = useState<Absence | null>(null);

  const originRef = useRef<DOMRect | null>(null);

  const all = useAtomValue(absencesAtom);
  const loading = useAtomValue(loadingAtom);
  const selectedId = useAtomValue(selectedIdAtom);
  const sameCaseIds = useAtomValue(sameCaseIdsAtom);
  const types = useAtomValue(absenceTypesAtom);
  const { drafts, slots } = useAtomValue(absenceManagerAtom);
  const locale = useIntlLocale();
  const select = useSetAtom(selectAbsenceAtom);
  const duplicate = useSetAtom(duplicateAbsenceAtom);
  const remove = useSetAtom(removeAbsenceAtom);

  const groups = useMemo(
    () => groupByYear(all.filter((absence) => absence.status === status)),
    [all, status],
  );

  const familyOfType = useMemo(
    () =>
      new Map<number, HourStatusFamily>(
        types.map((type) => [type.id, type.hourStatusFamily]),
      ),
    [types],
  );

  if (loading && 0 === all.length) {
    return <AbsenceRowsSkeleton />;
  }

  const range =
    null === confirming
      ? ""
      : formatRange(confirming.beginDate, confirming.endDate, locale);

  return (
    <>
      <List
        className={styles.list}
        fade="end"
        separators
        selectedValue={null === selectedId ? null : String(selectedId)}
        onSelectionChange={(value) => void select(Number(value))}
      >
        {0 === groups.length ? (
          <ListEmpty className={styles.empty}>
            <Trans>No absence in this period.</Trans>
          </ListEmpty>
        ) : (
          groups.map((group) => (
            <ListSection
              key={group.year}
              label={
                <Trans>
                  {group.year} · {group.absences.length} absences · {group.days}{" "}
                  days
                </Trans>
              }
            >
              {group.absences.map((absence) => (
                <ListItem
                  key={absence.id}
                  value={String(absence.id)}
                  className={
                    sameCaseIds.has(absence.id) ? styles.rowSameCase : undefined
                  }
                  startSection={
                    <span className={styles.rowDays}>
                      <Trans>{absence.dayCount} d</Trans>
                    </span>
                  }
                  endSection={
                    <span className={styles.rowEnd}>
                      {undefined !== drafts[absence.id] && (
                        <span
                          className={styles.rowDirty}
                          aria-label="Unsaved changes"
                        />
                      )}

                      <span className={styles.rowActions}>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t`Duplicate`}
                          leftSection={<Icon name="Copy" size="sm" />}
                          onClick={(event) => {
                            event.stopPropagation();
                            void duplicate(absence.id);
                          }}
                        />

                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t`Delete`}
                          leftSection={<Icon name="Trash2" size="sm" />}
                          onClick={(event) => {
                            event.stopPropagation();

                            if (isNewAbsence(absence.id)) {
                              void remove(absence.id);

                              return;
                            }

                            originRef.current =
                              event.currentTarget.getBoundingClientRect();

                            setConfirming(absence);
                          }}
                        />
                      </span>
                    </span>
                  }
                >
                  <span className={styles.rowRange}>
                    {formatRange(absence.beginDate, absence.endDate, locale)}
                  </span>

                  <span className={styles.rowMeta}>
                    <FamilyDot
                      family={familyOfType.get(absence.typeId ?? -1)}
                    />
                    {absence.typeLabel}
                    {coverage(absence, drafts[absence.id], slots[absence.id])}

                    {sameCaseIds.has(absence.id) && (
                      <span
                        className={styles.rowSameCaseMark}
                        title={t`Same case as the selected absence`}
                      >
                        <Icon name="Link2" size="sm" aria-hidden />
                        <Trans>same case</Trans>
                      </span>
                    )}
                  </span>
                </ListItem>
              ))}
            </ListSection>
          ))
        )}
      </List>

      <AlertDialog
        open={null !== confirming}
        originRef={originRef}
        onOpenChange={(open) => {
          if (!open) {
            setConfirming(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this absence?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                {range} and its replacements are removed for good. Saved
                absences cannot be brought back.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>

            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (null !== confirming) {
                  void remove(confirming.id);
                }
              }}
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function AbsenceList() {
  const tab = useAtomValue(tabAtom);
  const counts = useAtomValue(tabCountsAtom);
  const setTab = useSetAtom(setTabAtom);

  return (
    <Tabs
      className={styles.tabs}
      variant="segmented"
      size="sm"
      value={tab}
      onValueChange={(value) => void setTab(value as AbsenceStatus)}
    >
      <TabsList className={styles.tabsList}>
        {TABS.map((entry) => (
          <TabsTab key={entry.value} value={entry.value}>
            {entry.label}
            <Badge variant={entry.variant} size="sm" dot={false}>
              {counts[entry.value]}
            </Badge>
          </TabsTab>
        ))}

        <TabsIndicator />
      </TabsList>

      <TabsContent className={styles.tabsContent}>
        {TABS.map((entry) => (
          <TabsPanel
            key={entry.value}
            value={entry.value}
            className={styles.panel}
          >
            <AbsenceRows status={entry.value} />
          </TabsPanel>
        ))}
      </TabsContent>
    </Tabs>
  );
}
